import type { Params } from 'react-router-dom'
import { activeTabPath, type KeptTab } from './keptTabs'
import { recordTrace } from './perf'
import { startRouteVisit } from './perfResponsiveness'

// `navigation`: from the router publishing a new pathname to the first paint
// after the destination commits — covers lazy route chunks and kept-alive tab
// reveals, which a page's own mount-based trace can't see.

let pending: { pathname: string; wall: number; perf: number } | null = null
let committedPath: string | null = null
const seenTabs = new Set<string>()

export function markNavigationStart(pathname: string) {
  if (pathname === committedPath || pending?.pathname === pathname) return
  pending = { pathname, wall: Date.now(), perf: performance.now() }
}

// The route pattern, not the URL, so traces group by page: /events/:eventId.
export function routeLabel(pathname: string, params: Params): string {
  if ('*' in params) return '/*'
  const keyByValue = new Map(Object.entries(params).flatMap(([k, v]) => (v ? [[v, k] as const] : [])))
  return pathname.split('/').map(seg => {
    const key = keyByValue.get(decodeURIComponent(seg))
    return key ? `:${key}` : seg
  }).join('/') || '/'
}

function afterNextPaint(cb: () => void) {
  requestAnimationFrame(() => setTimeout(cb, 0))
}

export function markRouteCommitted(pathname: string, route: string, tabs: readonly KeptTab[]) {
  const tab = activeTabPath(tabs, pathname)
  const kind = tab ? (seenTabs.has(tab) ? 'tab_reveal' : 'tab_first') : 'page'
  if (tab) seenTabs.add(tab)
  startRouteVisit(route)
  const start = pending?.pathname === pathname ? pending : null
  pending = null
  committedPath = pathname
  if (!start) return
  afterNextPaint(() => recordTrace('navigation', start.wall, performance.now() - start.perf, { route, kind }))
}
